import {Box, Card, FAQ, FAQGroup, Grid, Section, Text} from '@primer/react-brand/esm'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {codeMarkRenderer} from '@github-ui/swp-core/components/contentful/shared/RichTextFieldRenderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {renderWithoutParagraphs, renderWithSpans} from '../../../utils'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import styles from './OutroSection.module.css'
import {BrandedDivider} from '../../../components/BrandedDivider'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {clsx} from 'clsx'
import {
  FlexSuiteTestimonialBanner,
  FlexSuiteTestimonialQuote,
  FlexSuiteTestimonialStat,
} from '../../../components/FlexSuiteTestimonial'
import {OutroStats} from './variants'

export const OutroSection = ({
  settings,
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  includeFaqs,
  faqHeading,
  outroTestimonial,
  outroTestimonialLink,
  outroTestimonialLogo,
  outroTestimonialQuote,
  outroTestimonialImage,
  outroTestimonialLabel,
  outroTestimonialStats,
  outroTestimonialName,
  outroTestimonialTitle,
}: OutroSectionProps) => {
  const brand = settings?.fields.brand
  const validFaqGroups = includeFaqs
    ? outroFaqs?.filter(group => group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer))
    : undefined
  const singleFaqGroup = validFaqGroups && validFaqGroups.length === 1 ? validFaqGroups[0] : null

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const hasStats = outroTestimonial === 'Stats' && Boolean(outroTestimonialStats?.length)
  const hasFullStatsTestimonial =
    hasStats && Boolean(outroTestimonialQuote && outroTestimonialLink && outroTestimonialImage)
  const hasTestimonial =
    (outroTestimonial === 'Quote' && Boolean(outroTestimonialQuote)) ||
    hasStats ||
    (outroTestimonial === 'Brand Banner' && Boolean(outroTestimonialQuote))

  if (
    !hasTestimonial &&
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    (!validFaqGroups || validFaqGroups.length === 0)
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && (
          <Box className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}>
            <Grid columnGap="none" rowGap="none" enableGutters={false}>
              <Grid.Column span={{xsmall: 12, medium: 10, xlarge: 12}} start={{medium: 2}}>
                <Grid columnGap="none" rowGap="none" enableGutters={false} className={baseStyles.narrowStackFrame}>
                  {outroCustomerStories.map(story => {
                    const Octicon = getPrimerIcon(story.fields.icon)
                    return (
                      <Grid.Column
                        key={story.sys.id}
                        span={{xsmall: 12, xlarge: 4}}
                        // eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals
                        className="gridColumn"
                      >
                        <Box
                          className={clsx(baseStyles.gridItem, baseStyles.stackedCardGridItem)}
                          padding={{narrow: 24, regular: 40, wide: 64}}
                          paddingInlineStart={{narrow: 24, regular: 80, wide: 64}}
                          paddingInlineEnd={{narrow: 24, regular: 80, wide: 64}}
                          paddingBlockStart={{regular: 80}}
                          paddingBlockEnd={{regular: 80}}
                          borderWidth="thin"
                          borderColor="muted"
                          style={{height: '100%'}}
                        >
                          <Card
                            href={story.fields.href}
                            variant="minimal"
                            fullWidth
                            ctaVariant="arrow"
                            ctaText={story.fields.label}
                          >
                            {Octicon && <Card.Icon icon={<Octicon />} color="default" hasBackground />}
                            {story.fields.eyebrow ? <Card.Label>{story.fields.eyebrow}</Card.Label> : null}
                            <Card.Heading>
                              {renderWithoutParagraphs(story.fields.heading)}
                              {story.fields.alt ? <span className="sr-only"> {story.fields.alt}</span> : null}
                            </Card.Heading>
                            <Card.Description>{renderWithoutParagraphs(story.fields.description)}</Card.Description>
                          </Card>
                        </Box>
                      </Grid.Column>
                    )
                  })}
                </Grid>
              </Grid.Column>
            </Grid>
          </Box>
        )}

        {outroTestimonial === 'Quote' && outroTestimonialQuote ? (
          <Box
            paddingBlockEnd={{
              narrow: 48,
              regular: 96,
            }}
            paddingBlockStart={{
              narrow: 48,
              regular: 96,
            }}
          >
            <Grid>
              <Grid.Column>
                <FlexSuiteTestimonialQuote
                  quote={outroTestimonialQuote}
                  name={outroTestimonialName}
                  title={outroTestimonialTitle}
                  link={outroTestimonialLink && outroTestimonialLink.fields}
                  logo={outroTestimonialLogo}
                />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {hasFullStatsTestimonial && outroTestimonialQuote && outroTestimonialLink && outroTestimonialImage ? (
          <Box marginBlockStart={48}>
            <FlexSuiteTestimonialStat
              quote={outroTestimonialQuote}
              link={outroTestimonialLink.fields}
              label={outroTestimonialLabel}
              image={outroTestimonialImage}
              stats={outroTestimonialStats}
            />
          </Box>
        ) : hasStats ? (
          <Box marginBlockStart={48}>
            <OutroStats stats={outroTestimonialStats} />
          </Box>
        ) : null}

        {outroTestimonial === 'Brand Banner' && outroTestimonialQuote ? (
          <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
            <FlexSuiteTestimonialBanner
              quote={outroTestimonialQuote}
              link={outroTestimonialLink && outroTestimonialLink.fields}
              logo={outroTestimonialLogo}
              image={outroTestimonialImage}
            />
          </Box>
        ) : null}

        {outroCtaBanner && (
          <Box
            className={clsx(baseStyles.gridFrameBottom, hasTestimonial && baseStyles.gridFrameTop)}
            marginBlockStart={hasTestimonial ? {narrow: 'spacious', regular: 64} : 'none'}
          >
            <FlexSuiteOutroCTA
              outroCtaHeading={outroCtaHeading}
              outroCtaDescription={outroCtaDescription}
              outroCtaOne={outroCtaOne}
              outroCtaTwo={outroCtaTwo}
            />
          </Box>
        )}

        {brand === 'Security' ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider brand={brand} />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {outroIncludeResources && (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        )}

        {validFaqGroups && validFaqGroups.length > 1 && (
          <Box
            paddingBlockStart={faqHeading ? undefined : {narrow: 'spacious', regular: 64}}
            paddingInlineStart={{narrow: 20, regular: 'none'}}
            paddingInlineEnd={{narrow: 20, regular: 'none'}}
          >
            <FAQGroup variant="gridline" defaultSelectedIndex={0}>
              {faqHeading && (
                <FAQGroup.Heading>
                  <Box
                    paddingBlockStart={{narrow: 'spacious', regular: 64}}
                    paddingInlineStart={{narrow: 'none', regular: 'spacious'}}
                  >
                    {renderWithSpans(faqHeading, 'd-block')}
                  </Box>
                </FAQGroup.Heading>
              )}

              {validFaqGroups.map(group => {
                const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

                return (
                  <FAQ key={group.sys.id} variant="gridline">
                    {group.fields.heading && <FAQ.Heading>{renderWithoutParagraphs(group.fields.heading)}</FAQ.Heading>}
                    {completeFaqs.map((faq, index) => (
                      <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
                        <FAQ.Question>{faq.fields.question}</FAQ.Question>
                        <FAQ.Answer>
                          <Text as="div" variant="muted">
                            {documentToReactComponents(faq.fields.answer, {renderMark: codeMarkRenderer})}
                          </Text>
                        </FAQ.Answer>
                      </FAQ.Item>
                    ))}
                  </FAQ>
                )
              })}
            </FAQGroup>
          </Box>
        )}

        {singleFaqGroup && (
          <Box
            className={faqHeading ? undefined : styles.StandaloneFaq}
            paddingBlockStart={faqHeading ? undefined : {narrow: 'spacious', regular: 64}}
            paddingInlineStart={{narrow: 20, regular: 'none'}}
            paddingInlineEnd={{narrow: 20, regular: 'none'}}
          >
            <FAQ key={singleFaqGroup.sys.id} variant="gridline">
              {faqHeading && (
                <FAQ.Heading>
                  <Box
                    paddingBlockStart={{narrow: 'spacious', regular: 64}}
                    paddingInlineStart={{narrow: 'none', regular: 'spacious'}}
                  >
                    {renderWithSpans(faqHeading, 'd-block')}
                  </Box>
                </FAQ.Heading>
              )}
              {!faqHeading && singleFaqGroup.fields.heading && (
                <FAQ.Heading>{renderWithoutParagraphs(singleFaqGroup.fields.heading)}</FAQ.Heading>
              )}
              {singleFaqGroup.fields.faqs
                .filter(faq => faq.fields.question && faq.fields.answer)
                .map((faq, index) => (
                  <FAQ.Item key={faq.sys.id} name={`faq-${singleFaqGroup.sys.id}`} open={index === 0}>
                    <FAQ.Question>{faq.fields.question}</FAQ.Question>
                    <FAQ.Answer>
                      <Text as="div" variant="muted">
                        {documentToReactComponents(faq.fields.answer, {renderMark: codeMarkRenderer})}
                      </Text>
                    </FAQ.Answer>
                  </FAQ.Item>
                ))}
            </FAQ>
          </Box>
        )}
      </Section>
    </Box>
  )
}
